import React, { useState, useContext, useEffect, useMemo, useCallback, useRef, Suspense } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import useBackButtonClose from "../hooks/useBackButtonClose";
import clsx from "clsx";
import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InputBase from "@mui/material/InputBase";
import CheckIcon from "@mui/icons-material/Check";
import useNavGroups from "../hooks/useNavGroups";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import api from "../services/api";
import MainListItems, { CATEGORY_ICONS } from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import LoadingSplash from "../components/LoadingSplash";
import UpdateBanner from "../components/UpdateBanner";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import GlobalSearch from '../components/GlobalSearch';
import BirthdayModal from "../components/BirthdayModal";
import logo from "../assets/logo.png";
import logoDark from "../assets/logo-black.png";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import useSocketListener from "../hooks/useSocketListener";
// import { FaGlobe } from "react-icons/fa"; // replaced by LanguageOutlinedIcon
import { logInfo, logError } from "../utils/logger";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
// Bottom nav (5 tabs fijos) -- misma variante "híbrida" aprobada en Agenda:
// outline delgado (stroke 1.5) en reposo, ícono sólido (Filled) en el
// activo. "Más" no tiene variante Filled en Tabler (los 3 puntos ya son
// sólidos por diseño), igual que en Agenda.
// IconUsers (plural) en vez de IconUser (singular) para "Contactos" -- evita
// la ambigüedad con "perfil de usuario" que señaló el negocio. Sin variante
// Filled en Tabler (igual que "Más"), así que Contactos también se queda en
// outline en su estado activo, sin romper nada -- ya es un patrón conocido.
import { IconLayoutDashboard, IconLayoutDashboardFilled, IconMessageCircle, IconMessageCircleFilled, IconLayoutKanban, IconLayoutKanbanFilled, IconUsers, IconDots } from "@tabler/icons-react";

const backendUrl = getBackendUrl();
const drawerWidth = 52;

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
      borderRadius: "50%", animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor", content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.4)", opacity: 0 },
  },
}));


const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 22,
  height: 22,
  border: `2px solid ${theme.palette.background.paper}`,
}));


const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [showOptions, setShowOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);

  const location = useLocation();
  const isInTicketChat = /\/tickets\/[^/]+/.test(location.pathname);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("md"));
  const isMobileLayout = !greaterThenSm;
  const navigate = useNavigate();

  // Catch-all a "Más" (índice 4) para cualquier ruta que no sea una de las
  // 4 principales -- antes retornaba false y ningún ítem se resaltaba en
  // páginas secundarias (ej. /users, /settings), ni siquiera "Más". Mismo
  // criterio ya aprobado en el bottom nav de Agenda.
  const getBottomNavValue = () => {
    const path = location.pathname;
    if (path.startsWith("/tickets") || path.startsWith("/chats")) return 0;
    if (path === "/") return 1;
    if (path.startsWith("/Kanban") || path.startsWith("/kanban")) return 2;
    if (path.startsWith("/contacts")) return 3;
    return 4;
  };

  const { navGroups } = useNavGroups();

  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  // Bandera explicita: se activa justo antes de un navigate() intencional
  // desde una fila del menu "Mas"/"Fijados" para que el cleanup del hook
  // sepa, sin ambiguedad ni dependencia de timing, que no debe tratar este
  // cierre como un "Atras"/"Cancelar".
  const navigatingAwayRef = useRef(false);
  useBackButtonClose(moreMenuOpen, () => setMoreMenuOpen(false), navigatingAwayRef);
  // RDS-MORE-MENU-FIX2: "Atencion" (uso diario: Tickets/Kanban/Chats/
  // Contactos/Estados WA) arranca expandida -- el resto sigue colapsado.
  // Sin esto, el primer tap de un usuario sobre cualquier item de una
  // seccion colapsada solo la abre (no navega), lo que se percibia como
  // "no pasa nada al tocar".
  // Persistido en localStorage -- mismo patron que "Fijados" (morePinnedPaths):
  // lo que el usuario expande/colapsa a mano sobrevive a un reload. Si no hay
  // nada guardado todavia (primera vez), arranca con "Atención" expandida.
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("moreExpandedSections"));
      return Array.isArray(stored) ? stored : ["Atención"];
    } catch (e) {
      return ["Atención"];
    }
  });

  useEffect(() => {
    localStorage.setItem("moreExpandedSections", JSON.stringify(expandedSections));
  }, [expandedSections]);

  const [moreSearchQuery, setMoreSearchQuery] = useState("");
  const [pinnerAnchorEl, setPinnerAnchorEl] = useState(null);
  const [pinnedPaths, setPinnedPaths] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("morePinnedPaths"));
      return Array.isArray(stored) && stored.length > 0 ? stored : ["/tickets", "/"];
    } catch {
      return ["/tickets", "/"];
    }
  });

  useEffect(() => {
    localStorage.setItem("morePinnedPaths", JSON.stringify(pinnedPaths));
  }, [pinnedPaths]);

  const togglePinned = (path) => {
    setPinnedPaths(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };

  const allMoreItems = useMemo(
    // .filter(!hidden): mismo criterio que MainListItems.js (desktop) aplica
    // en NavGroup/SubIconBtn -- una sola fuente de verdad para que "Fijados"
    // y el picker de fijado nunca ofrezcan una pagina que el sidebar oculta.
    () => navGroups.flatMap(g => g.items.filter(it => !it.hidden).map(it => ({ ...it, _groupColor: g.color, _groupBg: g.bg }))),
    [navGroups]
  );

  const moreSearchLower = moreSearchQuery.trim().toLowerCase();
  const visibleNavGroups = useMemo(() => {
    if (!moreSearchLower) return navGroups;
    return navGroups
      .map(g => ({ ...g, items: g.items.filter(it => it.label.toLowerCase().includes(moreSearchLower)) }))
      .filter(g => g.items.length > 0);
  }, [navGroups, moreSearchLower]);

  // Mismo criterio que activeCatForRoute en MainListItems.js (desktop):
  // la categoria "activa" es la que contiene la pagina actual, sin importar
  // si esta expandida o no en este momento.
  const activeCatForRoute = useMemo(() => {
    for (const g of navGroups) {
      if (g.items.some(i => i.to === location.pathname || (i.to !== "/" && location.pathname.startsWith(i.to)))) {
        return g.id;
      }
    }
    return null;
  }, [navGroups, location.pathname]);

  // Auto-expandir (sin colapsar nada mas) la categoria de la pagina actual --
  // corre en cada cambio de ruta Y en el primer render (activeCatForRoute ya
  // tiene su valor real desde el mount, derivado de location.pathname), asi
  // que cubre tanto "cambie de pagina" como "recargue la app". Nunca saca
  // nada de expandedSections, solo agrega si falta -- no pisa lo que el
  // usuario haya abierto/cerrado a mano en otras categorias.
  useEffect(() => {
    if (!activeCatForRoute) return;
    const activeLabel = navGroups.find(g => g.id === activeCatForRoute)?.label;
    if (!activeLabel) return;
    setExpandedSections(prev => prev.includes(activeLabel) ? prev : [...prev, activeLabel]);
  }, [activeCatForRoute, navGroups]);

  const toggleSection = (label) => {
    setExpandedSections(prev =>
      prev.includes(label)
        ? prev.filter(s => s !== label)
        : [...prev, label]
    );
  };

  // Fila navegable de un item del menu "Más" -- extraida para reusarla
  // tanto dentro del acordeon (secciones de 2+ items) como para el caso de
  // seccion de 1 solo item, que se muestra directo sin encabezado.
  const renderMoreMenuRow = (item, section) => {
    // Mismo criterio e igual acento azul que NavItem en MainListItems.js
    // (desktop): background rgba(3,153,255,0.09), barra izquierda 2.5x18px
    // #0399FF (mas chica que la de categoria, 3x22px, para mantener la
    // jerarquia visual), icono y label en #0399FF.
    const isActive = location.pathname === item.to ||
      (item.to !== "/" && location.pathname.startsWith(item.to));
    return (
      <Box
        key={item.to}
        onClick={() => {
          navigatingAwayRef.current = true;
          navigate(item.to);
          setMoreMenuOpen(false);
        }}
        sx={{
          display: "flex", alignItems: "center", gap: "12px",
          padding: "9px 16px", cursor: "pointer", position: "relative",
          borderRadius: "8px",
          background: isActive ? "rgba(3,153,255,0.09)" : "transparent",
          "&:active": { background: isActive ? "rgba(3,153,255,0.11)" : (theme.palette.mode==="dark"?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)") },
          ...(isActive && {
            "&::before": {
              content: '""', position: "absolute",
              left: 0, top: "50%", transform: "translateY(-50%)",
              width: "2.5px", height: "18px",
              background: "#0399FF", borderRadius: "0 2px 2px 0",
            },
          }),
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: "9px", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : section.bg,
        }}>
          {React.cloneElement(item.icon, { sx: {
            fontSize: 18,
            color: isActive ? "#0399FF" : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.5)" : section.color),
          } })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 14, fontWeight: isActive ? 600 : 500,
            color: isActive ? "#0399FF" : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.75)"),
          }}>
            {item.label}
          </Typography>
          <Typography sx={{
            fontSize: 11,
            color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.38)",
          }}>
            {item.sub}
          </Typography>
        </Box>
        <Typography sx={{
          fontSize: 16,
          color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)",
          flexShrink: 0,
        }}>›</Typography>
      </Box>
    );
  };

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const settings = useSettings();

  const fetchAnnouncements = useCallback(async (signal) => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: { status: true, pageNumber: "1" },
          signal,
        });
        const active = data.records.filter(a => {
          const isActive = a.status === true || a.status === "true";
          const isNotExpired = !a.expiresAt || new Date(a.expiresAt) > new Date();
          return isActive && isNotExpired;
        });
        setAnnouncements(active);
        setShowAnnouncementsModal(active.length > 0);
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") toastError(err);
      }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const controller = new AbortController();
    fetchAnnouncements(controller.signal);
    return () => controller.abort();
  }, [user?.id, fetchAnnouncements]);

  // Atualiza checkbox ao trocar de aviso
  useEffect(() => {
    if (!selectedAnnouncement) {
      setAckChecked(false);
      return;
    }
    // Não precisamos mais ler localStorage; tratamos via backend
    setAckChecked(false);
  }, [selectedAnnouncement, user?.companyId]);

  const handleToggleAcknowledge = async (announcementId, checked) => {
    try {
      if (checked) {
        await api.post(`/announcements/${announcementId}/ack`);
        // Remove este aviso da lista
        setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
        setSelectedAnnouncement(null);
      } else {
        await api.delete(`/announcements/${announcementId}/ack`);
        // Opcional: recarregar lista para reexibir (se desejar permitir desfazer)
        await fetchAnnouncements();
      }
      // Fecha modal se não restarem avisos
      setShowAnnouncementsModal((prev) => {
        return announcements.length - 1 > 0;
      });
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    

    if (document.body.offsetWidth > 600) {
      if (user.defaultMenu === "closed") {
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
      }
    }
    if (user.defaultTheme === "dark" && theme.palette.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, document.body.offsetWidth]);


  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Tu cuenta fue accedida desde otro dispositivo.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de usuário recebido", { data });
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de contato recebido", { data });
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          logInfo("Usuário faz aniversário hoje; exibindo modal");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          logInfo("Há aniversariantes hoje", { birthdayData });
        }
      } catch (error) {
        logError("Erro ao verificar aniversários", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  useEffect(() => {
    if (!user?.id || !user?.companyId) return;
    let mounted = true;
    const timer = setTimeout(() => {
      if (mounted) checkBirthdaysOnLogin();
    }, 1000);
    return () => { mounted = false; clearTimeout(timer); };
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600 || user.defaultMenu === "closed") {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  const LANGUAGE_OPTIONS = [
    { code: "pt", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    if (!user?.companyId) return;
    let mounted = true;
    async function fetchSettings() {
      try {
        const settings = await getAll();
        if (!mounted) return;
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt", "en"];
        try {
          if (enabledLanguagesSetting) langs = JSON.parse(enabledLanguagesSetting);
        } catch { }
        setEnabledLanguages(langs);
      } catch { }
    }
    fetchSettings();
    return () => { mounted = false; };
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  // ── Search state for sidebar
  const [searchQuery, setSearchQuery] = useState("");
  const [openCat, setOpenCat] = useState(null);

  if (loading || updateInProgress) {
    return <LoadingSplash />;
  }

  // ── Current page title for breadcrumb
  const PAGE_TITLES = {
    "/tickets": "Tickets", "/contacts": "Contactos", "/kanban": "Kanban",
    "/chats": "Chat interno", "/schedules": "Programas",
    "/quick-messages": "Respuestas rápidas", "/tags": "Etiquetas",
    "/reports": "Reportes", "/moments": "Tiempo real",
    "/campaigns": "Campañas", "/contact-lists": "Listas",
    "/campaigns-config": "Config campañas", "/files": "Archivos",
    "/flowbuilders": "Flujo conversación", "/phrase-lists": "Frases automáticas",
    "/connections": "Conexiones", "/users": "Usuarios",
    "/queues": "Departamentos", "/queue-integration": "Integraciones",
    "/prompts": "Prompts IA", "/settings": "Configuración",
    "/financeiro": "Facturas", "/messages-api": "API Mensajes",
    "/wallets": "Wallets", "/birthday-settings": "Cumpleaños",
    "/announcements": "Boletines", "/allConnections": "Todas conexiones",
    "/companies": "Empresas", "/helps": "Ayuda",
    "/": "Dashboard",
  };
  const currentTitle = Object.entries(PAGE_TITLES).find(([path]) =>
    path !== "/" ? location.pathname.startsWith(path) : location.pathname === "/"
  )?.[1] || "Shessai";

  const drawerWidth = drawerOpen ? 220 : 52;


  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>

      {/* ── SIDEBAR ─────────────────────────────────────────────── */}
      {!isMobileLayout && !hideMenu && (
        <Box sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: "width 0.18s cubic-bezier(0.4,0,0.2,1)",
          overflow: "hidden",
          zIndex: theme.zIndex.drawer,
          position: "relative",
        }}>
          <Box sx={{
            width: drawerWidth,
            height: "100vh",
            position: "fixed",
            top: 0, left: 0,
            transition: "width 0.18s cubic-bezier(0.4,0,0.2,1)",
            overflow: "hidden",
            background: "rgba(13,22,40,0.98)",
            borderRight: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"}`,
            display: "flex",
            flexDirection: "column",
            zIndex: theme.zIndex.drawer,
          }}>
            {/* Sidebar header */}
            <Box sx={{
              display: "flex", alignItems: "center",
              gap: drawerOpen ? "8px" : 0,
              px: drawerOpen ? "10px" : "14px",
              minHeight: 46, flexShrink: 0,
              borderBottom: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)"}`,
              overflow: "hidden",
              transition: "padding 0.18s ease",
            }}>
              <IconButton
                onClick={() => setDrawerOpen(!drawerOpen)}
                size="small"
                sx={{ color: "rgba(255,255,255,0.40)", p: "5px", flexShrink: 0, "&:hover": { color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.75)":"rgba(0,0,0,0.75)"), background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)") } }}
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="0.75" y="0.75" width="14.5" height="14.5" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.2"/>
                  <rect x="0.75" y="0.75" width="4.5" height="14.5" rx="2.5" fill="currentColor" opacity="0.30"/>
                  <line x1="5.25" y1="0.75" x2="5.25" y2="15.25" stroke="currentColor" strokeWidth="1.0"/>
                </svg>
              </IconButton>
              {drawerOpen && (
                <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden", flex: 1 }}>
                  <Typography sx={{
                    color: "rgba(255,255,255,0.88)", fontSize: 22, fontWeight: 600,
                    letterSpacing: "-0.01em", whiteSpace: "nowrap",
                  }}>
                    Shessai
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Nav items */}
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <MainListItems
                drawerOpen={drawerOpen}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onProfileClick={handleMenu}
                onCatChange={setOpenCat}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* ── MAIN CONTENT ────────────────────────────────────────── */}
      <Box component="main" sx={{
          flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0,
          ml: (!drawerOpen && openCat) ? "80px" : 0,
          transition: "margin-left 0.2s cubic-bezier(0.4,0,0.2,1)",
          
        }}>

        {/* Top bar — breadcrumb + utility icons */}
        {!hideMenu && (
          <Box sx={{
            height: 44, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: "16px", gap: 1,
            background: theme.palette.mode === "dark" ? "#0B0F1A" : "#ffffff",
            borderBottom: `0.5px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)"}`,
          }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <Typography sx={{
                color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.32)",
                fontSize: 12, fontWeight: 400,
              }}>Shessai</Typography>
              <Typography sx={{
                color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.18)",
                fontSize: 12,
              }}>/</Typography>
              <Typography sx={{
                color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.72)",
                fontSize: 12, fontWeight: 500, letterSpacing: "-0.01em",
              }}>
                {currentTitle}
              </Typography>
            </Box>

            {/* Utility icons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {greaterThenSm && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "1px" }}>
                  {/* VersionControl oculto del topbar */}
                  {/* <VersionControl onUpdateStart={handleUpdateStart} onUpdateComplete={handleUpdateComplete} /> */}
                  <div style={{ position: "relative", display: "inline-block" }} className="language-dropdown">
                    <button onClick={() => setShowOptions(!showOptions)} style={{
                      background: "none", border: "none",
                      color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                      cursor: "pointer", fontSize: "16px", padding: 0,
                      width: 34, height: 34, borderRadius: "9px", lineHeight: 1,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)",
                    }}><LanguageOutlinedIcon sx={{ fontSize: 18 }} /></button>
                    {showOptions && (
                      <div style={{
                        position: "absolute", top: "38px", right: 0,
                        background: theme.palette.mode === "dark" ? "var(--sh-surface, #111827)" : "#fff",
                        boxShadow: `0 4px 16px ${theme.palette.mode==="dark"?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.12)"}`,
                        borderRadius: "10px", padding: "6px", zIndex: 1000, minWidth: "130px",
                        border: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,
                      }}>
                        {filteredLanguageOptions.map((lang) => (
                          <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} style={{
                            background: "none", border: "none", cursor: "pointer",
                            display: "block", width: "100%", padding: "7px 10px",
                            fontSize: 12, color: theme.palette.text.primary, textAlign: "left",
                            borderRadius: "6px",
                          }}>{lang.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <IconButton size="small" onClick={handleRefreshPage} sx={{
                    width: 34, height: 34, borderRadius: "9px",
                    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                    transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)",
                    "&:hover": { background: "rgba(3,153,255,0.12)", color: "#38bdf8", transform: "translateY(-1px)" },
                  }}>
                    <RefreshOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>
              )}
              <IconButton size="small" onClick={colorMode.toggleColorMode} sx={{
                width: 34, height: 34, borderRadius: "9px",
                color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)",
                "&:hover": { background: "rgba(3,153,255,0.12)", color: "#38bdf8", transform: "translateY(-1px)" },
              }}>
                {theme.palette.mode === "dark" ? <LightModeOutlinedIcon sx={{ fontSize: 17 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 17 }} />}
              </IconButton>
              {greaterThenSm && (
                <Box sx={{
                  width: "1px", height: "18px",
                  background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                  mx: "6px",
                }} />
              )}
              {greaterThenSm && (
                <Box sx={{
                  display: "flex", alignItems: "center",
                  "& .MuiIconButton-root": {
                    width: 34, height: 34, borderRadius: "9px",
                    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                    transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)",
                    "&:hover": { background: "rgba(3,153,255,0.12)", color: "#38bdf8", transform: "translateY(-1px)" },
                  },
                }}>
                  <NotificationsVolume setVolume={setVolume} volume={volume} />
                </Box>
              )}
              {greaterThenSm && (
                <Box sx={{
                  width: "1px", height: "18px",
                  background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                  mx: "6px",
                }} />
              )}
              {!greaterThenSm && (
                <Box sx={{
                  width: "1px", height: "16px",
                  background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
                  mx: "3px",
                }} />
              )}
              <Box sx={{
                display: "flex", alignItems: "center", gap: "1px",
                "& .MuiIconButton-root": {
                  width: 34, height: 34, borderRadius: "9px",
                  color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)",
                  transition: "all 0.2s cubic-bezier(0.16,1,0.3,1)",
                  "&:hover": { background: "rgba(3,153,255,0.12)", color: "#38bdf8", transform: "translateY(-1px)" },
                },
                "& svg": {
                  fontSize: "17px !important",
                },
              }}>
                <ChatPopover />
                {user.id && <NotificationsPopOver volume={volume} />}
                <AnnouncementsPopover />
              </Box>
            </Box>
          </Box>
        )}

        <UpdateBanner />

        {/* Page content */}
        <Box sx={{ flex: 1, overflow: "auto", position: "relative" }}>
          <Suspense fallback={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <LoadingSplash />
            </Box>
          }>
            <Outlet />
          </Suspense>
        </Box>
      </Box>

      <GlobalSearch />
      {/* User menu */}
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onImageUpdate={(url) => setProfileUrl(url)}
        userId={user?.id}
      />
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={menuOpen}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            minWidth: 220,
            borderRadius: "12px",
            border: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,
            boxShadow: `0 8px 30px ${theme.palette.mode==="dark"?"rgba(255,255,255,0.10)":"rgba(0,0,0,0.10)"}`,
            mt: "6px",
            overflow: "visible",
            p: "6px",
          }
        }}
      >
        <Box sx={{
          px: "12px", pt: "10px", pb: "10px",
          display: "flex", alignItems: "center", gap: "10px",
          borderBottom: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`,
          mb: "4px",
        }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: "50%",
            background: "linear-gradient(135deg, #0399FF 0%, #0284d4 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
              fontSize: 13, fontWeight: 600,
              color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.82)":"rgba(0,0,0,0.82)"),
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 140,
            }}>
              {user?.name || "Usuario"}
            </Typography>
            <Typography sx={{
              fontSize: 11, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.38)":"rgba(0,0,0,0.38)"),
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 140,
            }}>
              {user?.email || ""}
            </Typography>
          </Box>
        </Box>

        <MenuItem
          onClick={handleOpenUserModal}
          sx={{
            borderRadius: "8px",
            px: "10px", py: "8px",
            gap: "10px",
            fontSize: 13,
            color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.72)":"rgba(0,0,0,0.72)"),
            "&:hover": { background: "rgba(3,153,255,0.06)" },
            transition: "background 0.15s",
          }}
        >
          <Box sx={{
            width: 28, height: 28, borderRadius: "7px",
            background: "#eff6ff",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <PersonOutlineIcon sx={{ fontSize: 16, color: "#0399FF" }} />
          </Box>
          {i18n.t("mainDrawer.appBar.user.profile")}
        </MenuItem>

        <Divider sx={{ my: "4px", borderColor: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)") }} />

        <MenuItem
          onClick={handleClickLogout}
          sx={{
            borderRadius: "8px",
            px: "10px", py: "8px",
            gap: "10px",
            fontSize: 13,
            color: "rgba(220,53,69,0.85)",
            "&:hover": { background: "rgba(220,53,69,0.06)" },
            transition: "background 0.15s",
          }}
        >
          <Box sx={{
            width: 28, height: 28, borderRadius: "7px",
            background: "rgba(220,53,69,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <LogoutIcon sx={{ fontSize: 16, color: "rgba(220,53,69,0.85)" }} />
          </Box>
          {i18n.t("mainDrawer.appBar.user.logout")}
        </MenuItem>
      </Menu>

      {/* Announcements modal */}
      <Dialog open={showAnnouncementsModal} onClose={() => setShowAnnouncementsModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Comunicados</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>{selectedAnnouncement.title}</Typography>
              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>{selectedAnnouncement.text}</Typography>
              <FormControlLabel style={{ marginTop: 12 }} control={
                <Checkbox color="primary" checked={ackChecked} onChange={(e) => {
                  setAckChecked(e.target.checked);
                  handleToggleAcknowledge(selectedAnnouncement.id, e.target.checked);
                }} />
              } label="Estoy al tanto — no mostrar de nuevo" />
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`} alt="Anexo" style={{ maxWidth: "100%" }} />
                </div>
              )}
              <Button onClick={() => setSelectedAnnouncement(null)} style={{ marginTop: 16 }} variant="outlined">Volver</Button>
            </div>
          ) : (
            <List>
              {announcements.map((a) => (
                <ListItem button key={a.id} onClick={() => setSelectedAnnouncement(a)}>
                  <ListItemAvatar><Avatar><NotificationsIcon /></Avatar></ListItemAvatar>
                  <ListItemText
                    primary={a.title}
                    secondary={<>
                      <Typography component="span" variant="body2" color="textPrimary">
                        Prioridad: {a.priority === 1 ? "Alta" : a.priority === 2 ? "Media" : "Baja"}
                      </Typography>
                      {` — ${new Date(a.createdAt).toLocaleDateString()}`}
                    </>}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAnnouncementsModal(false)} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Birthday modal */}
      <BirthdayModal open={showBirthdayModal} onClose={() => setShowBirthdayModal(false)} user={user} />


      {/* ── BOTTOM NAVIGATION (móvil) ───────────────────────────── */}
      {/* Ver TAREA: paridad visual con el menú inferior de Citas (Opción B
          aprobada) -- fondo de cristal esmerilado (degradado + blur,
          adaptado a tema) reemplazando el fondo sólido de antes, e ícono/
          etiqueta como ÚNICA señal de estado (sin placa cuadrada
          permanente ni pill de fondo en el activo) -- mismos valores
          exactos ya afinados en Citas (agenda-shessai/MobileNav.jsx), no
          aproximados. Los 5 tabs y sus íconos de @tabler/icons-react no
          cambian, solo el tratamiento visual del contenedor/ícono/label. */}
      {isMobileLayout && !hideMenu && (
        <>
          <Box sx={{
            position: "fixed",
            bottom: 0, left: 0, right: 0,
            zIndex: 1300,
            background: theme.palette.mode === "dark"
              ? "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 60%, rgba(0,0,0,0.85) 100%)"
              : "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 60%, rgba(255,255,255,0.88) 100%)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            // Fallback -- navegadores/SO sin soporte real de backdrop-filter
            // (algunos Android WebView viejos, Firefox desktop antes de
            // v103) usan el tono más sólido del propio degradado de forma
            // PLANA, sin degradado ni blur -- mismo criterio que Citas: se
            // prioriza legibilidad sobre el efecto visual cuando el
            // navegador no puede dar ninguno de los dos.
            "@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))": {
              background: theme.palette.mode === "dark" ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.88)",
            },
            pt: "6px", pb: "calc(env(safe-area-inset-bottom) + 6px)", minHeight: 58,
          }}>
            {(() => {
              const navValue = moreMenuOpen ? 4 : getBottomNavValue();
              const inactiveColor = theme.palette.mode === "dark" ? "#5B6572" : "#4B5563";
              // Sin placa ni pill -- el color es la ÚNICA señal de estado
              // (igual que Citas). Wrapper 32×32 sin fondo, solo para
              // centrar el ícono de 27px sin recortarlo.
              const navIcon = (Outline, Filled, isOn) => {
                const Icon = (isOn && Filled) ? Filled : Outline;
                return (
                  <Box sx={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", mb: "3px" }}>
                    <Icon size={27} stroke={1.8} color={isOn ? "#0399FF" : inactiveColor} style={{ transition: "color 0.12s ease" }} />
                  </Box>
                );
              };
              return (
                <BottomNavigation
                  value={navValue}
                  showLabels
                  onChange={(event, newValue) => {
                    const routes = ["/tickets", "/", "/Kanban", "/contacts"];
                    if (routes[newValue]) navigate(routes[newValue]);
                  }}
                  sx={{
                    height: 56,
                    background: "transparent",
                    "& .MuiBottomNavigationAction-root": {
                      minWidth: 0,
                      padding: "5px 3px",
                      margin: "0 3px",
                      color: inactiveColor,
                      transition: "color 0.12s ease",
                      "&.Mui-selected": { color: "#0399FF" },
                    },
                    // showLabels mantiene la etiqueta visible en los 5 ítems,
                    // no solo el activo -- mismo criterio de accesibilidad ya
                    // aprobado en Agenda (el público de Shessai no siempre
                    // reconoce los íconos sin texto).
                    "& .MuiBottomNavigationAction-label": {
                      fontSize: "10px",
                      fontWeight: 400,
                      opacity: 1,
                      "&.Mui-selected": { fontSize: "10px", fontWeight: 700 },
                    },
                  }}
                >
                  <BottomNavigationAction label="Tickets" icon={navIcon(IconMessageCircle, IconMessageCircleFilled, navValue === 0)} />
                  <BottomNavigationAction label="Dashboard" icon={navIcon(IconLayoutDashboard, IconLayoutDashboardFilled, navValue === 1)} />
                  <BottomNavigationAction label="Kanban" icon={navIcon(IconLayoutKanban, IconLayoutKanbanFilled, navValue === 2)} />
                  <BottomNavigationAction label="Contactos" icon={navIcon(IconUsers, null, navValue === 3)} />
                  <BottomNavigationAction
                    label="Más"
                    icon={navIcon(IconDots, null, navValue === 4)}
                    onClick={(e) => { e.stopPropagation(); setMoreMenuOpen(true); }}
                  />
                </BottomNavigation>
              );
            })()}
          </Box>

          {/* Drawer "Más" — todas las secciones */}
          <Drawer
            anchor="bottom"
            open={moreMenuOpen}
            onClose={() => setMoreMenuOpen(false)}
            // RDS-BOTTOMSHEET-FIX2: mismo defecto encontrado en el modal de
            // Aceptar Chat -- PaperProps.zIndex no compite contra el
            // BottomNavigation (elementos hermanos, contextos de apilamiento
            // distintos). El zIndex real va en ModalProps (el root fixed).
            ModalProps={{ sx: { zIndex: 1301 } }}
            PaperProps={{ sx: {
              borderRadius: "16px 16px 0 0",
              maxHeight: "85vh",
              zIndex: 1301,
              background: theme.palette.mode === "dark" ? "#0f172a" : "#fff",
            }}}
          >
            <Box sx={{ overflowY: "auto", pb: "env(safe-area-inset-bottom, 24px)" }}>
              {/* Handle */}
              <Box sx={{ display: "flex", justifyContent: "center", pt: "10px", pb: "4px" }}>
                <Box sx={{ width: 36, height: 4, borderRadius: "2px", background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.15)") }} />
              </Box>

              {/* Buscador */}
              <Box sx={{ px: "16px", pb: "10px" }}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: "8px",
                  background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                  borderRadius: "10px", px: "12px", py: "8px",
                }}>
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)" }} />
                  <InputBase
                    value={moreSearchQuery}
                    onChange={(e) => setMoreSearchQuery(e.target.value)}
                    placeholder="Buscar en el menú..."
                    sx={{ flex: 1, fontSize: 14, color: theme.palette.text.primary }}
                  />
                  {moreSearchQuery && (
                    <CloseIcon
                      onClick={() => setMoreSearchQuery("")}
                      sx={{ fontSize: 16, cursor: "pointer", color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)" }}
                    />
                  )}
                </Box>
              </Box>

              {/* Fijados */}
              {!moreSearchQuery && (
                <Box sx={{ px: "16px", pb: "12px" }}>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em",
                    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
                    mb: "8px",
                  }}>
                    Fijados
                  </Typography>
                  <Box sx={{ display: "flex", gap: "10px", overflowX: "auto", pb: "2px" }}>
                    {pinnedPaths.map((path) => {
                      const item = allMoreItems.find(it => it.to === path);
                      if (!item) return null;
                      return (
                        <Box
                          key={path}
                          onClick={() => {
          navigatingAwayRef.current = true;
          navigate(item.to);
          setMoreMenuOpen(false);
        }}
                          sx={{
                            display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
                            flexShrink: 0, width: 60, cursor: "pointer",
                          }}
                        >
                          <Box sx={{
                            width: 44, height: 44, borderRadius: "12px",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : item._groupBg,
                          }}>
                            {React.cloneElement(item.icon, { sx: { fontSize: 20, color: item._groupColor } })}
                          </Box>
                          <Typography noWrap sx={{ fontSize: 10, textAlign: "center", color: theme.palette.text.secondary, width: "100%" }}>
                            {item.label}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Box
                      onClick={(e) => setPinnerAnchorEl(e.currentTarget)}
                      sx={{
                        display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
                        flexShrink: 0, width: 60, cursor: "pointer",
                      }}
                    >
                      <Box sx={{
                        width: 44, height: 44, borderRadius: "12px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: `1.5px dashed ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)"}`,
                      }}>
                        <AddIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                      </Box>
                      <Typography noWrap sx={{ fontSize: 10, textAlign: "center", color: theme.palette.text.secondary, width: "100%" }}>
                        Fijar
                      </Typography>
                    </Box>
                  </Box>
                  <Menu
                    anchorEl={pinnerAnchorEl}
                    open={Boolean(pinnerAnchorEl)}
                    onClose={() => setPinnerAnchorEl(null)}
                    // RDS-BOTTOMSHEET-FIX2: Menu no tiene ModalProps (a
                    // diferencia de Drawer/Dialog) -- en MUI 7 el slot del
                    // root fijo es slotProps.root. Sin esto cae al default
                    // theme.zIndex.modal=1300, por debajo del Drawer "Más"
                    // (ModalProps.zIndex:1301) que lo tapa.
                    slotProps={{ root: { sx: { zIndex: 1302 } } }}
                  >
                    {allMoreItems.map((item) => (
                      <MenuItem key={item.to} onClick={() => { togglePinned(item.to); setPinnerAnchorEl(null); }} sx={{ gap: "10px", fontSize: 13 }}>
                        {React.cloneElement(item.icon, { sx: { fontSize: 16, color: item._groupColor } })}
                        {item.label}
                        {pinnedPaths.includes(item.to) && (
                          <CheckIcon sx={{ fontSize: 16, ml: "auto", color: "#0399FF" }} />
                        )}
                      </MenuItem>
                    ))}
                  </Menu>
                  <Box sx={{ height: "0.5px", background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"), mt: "12px" }} />
                </Box>
              )}

              {visibleNavGroups.map((section) => {
                // Seccion vacia (ej. "Llamadas IA" para un usuario no-admin,
                // items:[]) -- no renderizar nada, ni encabezado ni fila.
                if (section.items.length === 0) return null;

                // Seccion de un solo item -- el encabezado plegable quedaria
                // duplicando el mismo texto del unico item de abajo (ej.
                // "Llamadas IA" arriba Y abajo), y encima el header nunca
                // navega, solo expande -- confundible con "no pasa nada al
                // tocar". Se salta el acordeon entero y se muestra la fila
                // navegable directo.
                if (section.items.length === 1) {
                  return renderMoreMenuRow(section.items[0], section);
                }

                const isExpanded = moreSearchQuery || expandedSections.includes(section.label);
                // Mismo azul exacto que el sidebar desktop usa para marcar
                // la categoria activa (MainListItems.js: background
                // rgba(3,153,255,0.15), color #0399FF, barra izquierda
                // #0399FF) -- no una aproximacion.
                const hasActive = section.id === activeCatForRoute;
                return (
                  <Box key={section.label} sx={{ px: "8px", pt: "4px" }}>
                    {/* Header acordeón -- fondo tintado + icono propio de
                        categoria (CATEGORY_ICONS, no el de la primera
                        pagina hija) + chevron real animado, para que se
                        distinga claramente de las filas de pagina de abajo. */}
                    <Box
                      onClick={() => toggleSection(section.label)}
                      sx={{
                        display: "flex", alignItems: "center", gap: "12px",
                        px: "10px", py: "9px", borderRadius: "10px", cursor: "pointer",
                        position: "relative",
                        background: hasActive
                          ? "rgba(3,153,255,0.15)"
                          : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)"),
                        "&:active": { opacity: 0.7 },
                        ...(hasActive && {
                          "&::before": {
                            content: '""', position: "absolute",
                            left: 0, top: "50%", transform: "translateY(-50%)",
                            width: "3px", height: "22px",
                            background: "#0399FF", borderRadius: "0 3px 3px 0",
                          },
                        }),
                      }}
                    >
                      <Box sx={{
                        width: 32, height: 32, borderRadius: "8px",
                        background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : section.bg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {React.cloneElement(CATEGORY_ICONS[section.id] || <SettingsOutlinedIcon />, { sx: {
                          fontSize: 17,
                          color: hasActive ? "#0399FF" : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : section.color),
                        } })}
                      </Box>
                      <Typography sx={{
                        fontSize: 13, fontWeight: 600, flex: 1,
                        color: hasActive ? "#0399FF" : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)"),
                      }}>
                        {section.label} · {section.items.length}
                      </Typography>
                      <ExpandMoreIcon sx={{
                        fontSize: 20,
                        color: hasActive ? "#0399FF" : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)"),
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        flexShrink: 0,
                      }} />
                    </Box>
                    {isExpanded && (
                      <Box sx={{ pl: "14px" }}>
                        {/* .filter(!hidden): mismo criterio que MainListItems.js
                            (NavGroup, linea 144) aplica en el sidebar desktop. */}
                        {section.items.filter(item => !item.hidden).map((item) => renderMoreMenuRow(item, section))}
                      </Box>
                    )}
                  </Box>
                );
              })}

{/* Configuración rápida — idioma / refresh / volumen (reusan handlers del topbar) */}
              <Box sx={{ height: "0.5px", background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"), mx: "16px", my: "8px" }} />
              <Typography sx={{
                fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em",
                color: "rgba(255,255,255,0.55)", px: "16px", pt: "6px", pb: "6px",
              }}>
                Configuración rápida
              </Typography>

              <Box sx={{ position: "relative" }}>
                <Box
                  onClick={() => setShowOptions(!showOptions)}
                  sx={{
                    display: "flex", alignItems: "center", gap: "12px",
                    padding: "10px 8px", mx: "8px", cursor: "pointer", borderRadius: "8px",
                    "&:active": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)") },
                  }}
                >
                  <LanguageOutlinedIcon sx={{ fontSize: 18, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)" }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.75)" }}>
                    Idioma
                  </Typography>
                </Box>
                {showOptions && (
                  <Box sx={{
                    mx: "16px", mb: "6px", borderRadius: "10px", padding: "6px",
                    background: theme.palette.mode === "dark" ? "var(--sh-surface, #111827)" : "#fff",
                    border: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,
                  }}>
                    {filteredLanguageOptions.map((lang) => (
                      <Box key={lang.code} onClick={() => handleLanguageChange(lang.code)} sx={{
                        padding: "8px 10px", fontSize: 13, color: theme.palette.text.primary,
                        borderRadius: "6px", cursor: "pointer",
                        "&:active": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)") },
                      }}>
                        {lang.label}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box
                onClick={() => { handleRefreshPage(); setMoreMenuOpen(false); }}
                sx={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "10px 8px", mx: "8px", cursor: "pointer", borderRadius: "8px",
                  "&:active": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)") },
                }}
              >
                <RefreshOutlinedIcon sx={{ fontSize: 18, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)" }} />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.75)" }}>
                  Refrescar
                </Typography>
              </Box>

              <Box sx={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "2px 8px", mx: "8px", borderRadius: "8px",
                "&:active": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)") },
              }}>
                <NotificationsVolume setVolume={setVolume} volume={volume} />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.75)" }}>
                  Volumen de notificaciones
                </Typography>
              </Box>

              {/* Divider + Mi cuenta + Cerrar sesión -- agrupados juntos
                  (ambas son acciones sobre la cuenta propia), separados por
                  el mismo divider de las filas de arriba (Idioma/Refrescar/
                  Volumen, ajustes de la app, no de la cuenta).
                  Ver TAREA: acceso móvil a "Acceso con Google" -- mismo
                  UserModal y mismo estado (userModalOpen/handleOpenUserModal)
                  que ya usa el menú del avatar en escritorio, montados sin
                  condicional de plataforma más abajo -- no hace falta un
                  segundo modal ni un mecanismo de acciones nuevo, solo un
                  disparador más en este pie que ya dispara acciones locales
                  de UI. */}
              <Box sx={{ height: "0.5px", background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"), mx: "16px", my: "8px" }} />
              <Box
                onClick={() => { handleOpenUserModal(); setMoreMenuOpen(false); }}
                sx={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "10px 8px", mx: "8px", cursor: "pointer", borderRadius: "8px",
                  "&:active": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.03)":"rgba(0,0,0,0.03)") },
                }}
              >
                <Box sx={{
                  width: 28, height: 28, borderRadius: "7px",
                  background: "#eff6ff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <PersonOutlineIcon sx={{ fontSize: 16, color: "#0399FF" }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.75)" }}>
                  {i18n.t("mainDrawer.appBar.user.profile")}
                </Typography>
              </Box>
              <Box
                onClick={() => { handleClickLogout(); setMoreMenuOpen(false); }}
                sx={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "9px 16px", cursor: "pointer",
                  "&:hover": { background: "rgba(220,53,69,0.04)" },
                }}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: "9px",
                  background: "rgba(220,53,69,0.08)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <LogoutOutlinedIcon sx={{ fontSize: 18, color: "rgba(220,53,69,0.8)" }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "rgba(220,53,69,0.8)" }}>
                  Cerrar sesión
                </Typography>
              </Box>
              <Box sx={{ height: "calc(64px + env(safe-area-inset-bottom))" }} />
            </Box>
          </Drawer>
        </>
      )}

      {/* Mobile overlay */}
      {!hideMenu && !greaterThenSm && drawerOpen && (
        <Box
          sx={{ position: "fixed", inset: 0, zIndex: theme.zIndex.drawer - 1, background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.45)":"rgba(0,0,0,0.45)") }}
          onClick={() => setDrawerOpen(false)}
        />
      )}
    </Box>
  );

};

export default LoggedInLayout;
